import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Checkbox } from '../../../components/Checkbox';
import { TextInput } from '../../../components/TextInput';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { baseStyleSheet, bs } from '../../../styles/styles';

const NAME_FORMAT_OPTIONS: { value: Api.OpportunityNameFormat; label: string }[] = [
	{ value: 'ContactAndCompany', label: 'Contact name and company (e.g., John Smith (Acme Inc))' },
	{ value: 'ContactOnly', label: 'Contact name only (e.g., John Smith)' },
	{ value: 'CompanyOnly', label: 'Company name only (e.g., Acme Inc)' },
];

interface OpportunityStepFormProps {
	/**
	 * The currently selected board.
	 */
	selectedBoard: Api.IBoard | null;
	/**
	 * The currently selected stage.
	 */
	selectedStage: Api.IBoardStage | null;
	/**
	 * Current deal size value (as string for input).
	 */
	dealSize: string;
	/**
	 * Whether to update existing opportunity instead of creating new.
	 */
	updateExisting: boolean;
	/**
	 * Current name-format selection for how the opportunity is named on the board.
	 */
	nameFormat: Api.OpportunityNameFormat;
	/**
	 * Available board options for selection.
	 */
	boardOptions: Api.IBoard[] | undefined;
	/**
	 * Available stage options for the selected board.
	 */
	stageOptions: Api.IBoardStage[];
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether data is being saved.
	 */
	isBusy: boolean;
	/**
	 * Whether boards are still loading.
	 */
	isLoadingBoards: boolean;
	/**
	 * Unique key for the form.
	 */
	baseKey: string;
	/**
	 * Callback when board selection changes.
	 */
	onBoardChange: (board: Api.IBoard, firstStage: Api.IBoardStage | null) => void;
	/**
	 * Callback when stage selection changes.
	 */
	onStageChange: (stage: Api.IBoardStage) => void;
	/**
	 * Callback when deal size changes.
	 */
	onDealSizeChange: (size: string) => void;
	/**
	 * Callback when deal size input blurs (trigger save).
	 */
	onDealSizeBlur: () => void;
	/**
	 * Callback when update existing checkbox changes.
	 */
	onUpdateExistingChange: (checked: boolean) => void;
	/**
	 * Callback when the opportunity name format selection changes.
	 */
	onNameFormatChange: (value: Api.OpportunityNameFormat) => void;
	/**
	 * Callback to clear error messages.
	 */
	onClearError: () => void;
}

/**
 * Form component for editing opportunity automation step content.
 * Displays board and stage selectors with optional deal size input.
 */
export function OpportunityStepForm({
	selectedBoard,
	selectedStage,
	dealSize,
	updateExisting,
	nameFormat,
	boardOptions,
	stageOptions,
	canEdit,
	isBusy,
	isLoadingBoards,
	baseKey,
	onBoardChange,
	onStageChange,
	onDealSizeChange,
	onDealSizeBlur,
	onUpdateExistingChange,
	onNameFormatChange,
	onClearError,
}: OpportunityStepFormProps) {
	const isDisabled = !canEdit || isBusy;

	const handleDealSizeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		onDealSizeChange(e.target.value);
		onClearError();
	};

	const handleUpdateExistingChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		onUpdateExistingChange(e.target.checked);
	};

	return (
		<>
			<Checkbox
				id={`automation-opportunity-step-updateExisting-checkbox-${baseKey}`}
				checked={updateExisting}
				type='large'
				onChange={handleUpdateExistingChanged}
				className={css(styleSheet.updateExistingLabel, baseStyleSheet.mt3)}
				disabled={isDisabled}
			>
				<div>
					<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
						Update an existing opportunity instead of creating a new one.
					</p>
				</div>
			</Checkbox>

			<div className={css(styleSheet.fieldLabel)}>
				Select an opportunity board: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<Select
				value={selectedBoard?.id ?? ''}
				onValueChange={id => {
					const board = boardOptions?.find(b => b.id === id);
					if (board) onBoardChange(board, board.stages?.[0] ?? null);
				}}
				disabled={isDisabled || isLoadingBoards}
				items={boardOptions
					?.filter(b => b.id != null)
					.map(board => ({
						label: board.name,
						value: board.id,
					}))}
			>
				<SelectTrigger styles={styleSheet.typeSelect}>
					<SelectValue placeholder='Select a choice below...' />
				</SelectTrigger>
				<SelectContent>
					{boardOptions
						?.filter(b => b.id != null)
						.map(board => (
							<SelectItem key={board.id} value={board.id!}>
								{board.name}
							</SelectItem>
						))}
				</SelectContent>
			</Select>

			<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>
				Select a stage: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<Select
				value={selectedStage?.id ?? ''}
				onValueChange={id => {
					const stage = stageOptions.find(s => s.id === id);
					if (stage) onStageChange(stage);
				}}
				disabled={isDisabled || !selectedBoard}
				items={
					stageOptions
						.filter(s => s.id != null)
						.map(stage => ({
							label: stage.name,
							value: stage.id,
						})) || []
				}
			>
				<SelectTrigger styles={styleSheet.typeSelect}>
					<SelectValue placeholder='Select a choice below...' />
				</SelectTrigger>
				<SelectContent>
					{stageOptions
						.filter(s => s.id != null)
						.map(stage => (
							<SelectItem key={stage.id} value={stage.id!}>
								{stage.name}
							</SelectItem>
						))}
				</SelectContent>
			</Select>

			<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>Opportunity name:</div>
			<Select
				value={nameFormat}
				onValueChange={value => onNameFormatChange(value as Api.OpportunityNameFormat)}
				disabled={isDisabled}
				items={NAME_FORMAT_OPTIONS.map(option => ({
					label: option.label,
					value: option.value,
				}))}
			>
				<SelectTrigger styles={styleSheet.typeSelect}>
					<SelectValue placeholder='Select a choice below...' />
				</SelectTrigger>
				<SelectContent>
					{NAME_FORMAT_OPTIONS.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<div className={css(bs.textXs, bs.textDarkGrayFontColor, baseStyleSheet.mt1)}>
				Controls how the opportunity is named on the board when this step creates it. If a contact doesn&apos;t have a
				company name on file, we fall back to the contact&apos;s name so the opportunity is never blank.
			</div>

			{!updateExisting && (
				<>
					<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>Amount: &nbsp;</div>
					<TextInput
						className={css(styleSheet.emailNameInput)}
						disabled={isDisabled}
						inputId={`automation-opportunity-step-amount-input-${baseKey}`}
						key={`${baseKey}-amount-input`}
						leftAccessory={<span className={css(styleSheet.dollar)}>$</span>}
						onBlur={onDealSizeBlur}
						onChange={handleDealSizeChanged}
						onFocus={onClearError}
						placeholder='0.00'
						type='text'
						value={dealSize || ''}
					/>
				</>
			)}
		</>
	);
}
